/**
 * QP-183 — investment capacity calculator math.
 *
 * Pure functions only (no db/env/logger imports). QL-140 moved this module
 * into the shared package so the three consumers cannot drift: the financial
 * situation service (authoritative save), the flow-completion engine (variant
 * selection) and the advisor widget's live preview.
 */

import type {
  CapacityInputFrequency,
  FinancialSituationModelSettings,
  InvestmentCapacityVariantConfig,
} from "./types/financial-situation-models";
import type {
  FinancialSituationInput,
  FinancialSituationResult,
  LiquidityBufferMode,
} from "./types/flow-instances";

const has = (v: number | null | undefined): v is number =>
  typeof v === "number" && Number.isFinite(v);

/**
 * Compute the investment capacity block from the advisor-entered triangle
 * (income / expenses / surplus), the liquidity buffer and short-term expenses,
 * against the model's variant config.
 *
 * - Triangle is server-authoritative: income + expenses always win and
 *   re-derive surplus; otherwise the missing third value is derived from the
 *   two present ones. Fewer than two inputs → surplus is null (capacity is
 *   incomplete; the completion engine reports `capacity_missing`).
 * - The entered buffer below the computed minimum does NOT fail the save —
 *   `bufferBelowMinimum` is flagged and the effective buffer is clamped to
 *   the minimum so `availableForInvestments` never overstates. Completion is
 *   gated by the engine (`buffer_below_minimum`).
 * - QP-184 — `memberCount` (Group clients: number of group members, else 1)
 *   scales the buffer FLOOR only (DAP parity: a shared household reserve
 *   grows per member; the income×multiplier term is already joint income).
 *   The configured floor is therefore an amount PER MEMBER. The widget calls
 *   this same function for its preview — there is no client-side copy.
 * - QL-107 — `fixedBuffer` (already resolved by the caller via the shared
 *   `resolveFixedBuffer`, so this module stays I/O-free) REPLACES the derived
 *   minimum outright: floor, multiplier and the entered buffer all stop
 *   participating, and it is used literally — no per-member scaling, because
 *   it comes from a policy about the client rather than a per-head reserve.
 *   The entered buffer is only read, never rewritten, so it returns to use if
 *   the client's flag is later removed.
 * - QL-127 — the financial situation's own entries win over the typed
 *   triangle, side by side (`capacityTriangleFromEntries`): a side with values
 *   is locked and flagged `*FromEntries`; a side without stays typed. With
 *   `inputFrequency: "annual"` the typed sides are `annualIncome` /
 *   `annualExpenses` ÷ 12 and the surplus is never typed. The multiplier term
 *   is `bufferMultiplierBasis` × multiplier (income by default). The buffer
 *   follows `liquidityBufferMode`: `minimum` ignores the entered buffer,
 *   `custom` uses it (clamped + flagged below the minimum as before; missing
 *   → `liquidityBufferMissing`, the minimum stands in and completion is
 *   blocked). The monthly results are left unrounded — consumers format.
 */
export function computeCapacity(
  input: FinancialSituationInput,
  config: InvestmentCapacityVariantConfig,
  memberCount = 1,
  fixedBuffer: number | null = null,
): NonNullable<FinancialSituationResult["capacity"]> {
  const fromEntries = capacityTriangleFromEntries(input);
  const incomeFromEntries = fromEntries.monthlyIncome !== null;
  const expensesFromEntries = fromEntries.monthlyExpenses !== null;

  let monthlyIncome: number | null;
  let monthlyExpenses: number | null;
  let monthlySurplus: number | null = null;

  if (config.inputFrequency === "annual") {
    monthlyIncome =
      fromEntries.monthlyIncome ?? (has(input.annualIncome) ? input.annualIncome / 12 : null);
    monthlyExpenses =
      fromEntries.monthlyExpenses ?? (has(input.annualExpenses) ? input.annualExpenses / 12 : null);
    if (monthlyIncome !== null && monthlyExpenses !== null) {
      monthlySurplus = monthlyIncome - monthlyExpenses;
    }
  } else {
    monthlyIncome =
      fromEntries.monthlyIncome ?? (has(input.monthlyIncome) ? input.monthlyIncome : null);
    monthlyExpenses =
      fromEntries.monthlyExpenses ?? (has(input.monthlyExpenses) ? input.monthlyExpenses : null);
    if (monthlyIncome !== null && monthlyExpenses !== null) {
      monthlySurplus = monthlyIncome - monthlyExpenses;
    } else if (monthlyIncome !== null && has(input.monthlySurplus)) {
      monthlySurplus = input.monthlySurplus;
      monthlyExpenses = monthlyIncome - monthlySurplus;
    } else if (monthlyExpenses !== null && has(input.monthlySurplus)) {
      monthlySurplus = input.monthlySurplus;
      monthlyIncome = monthlyExpenses + monthlySurplus;
    }
  }

  const multiplierBasis =
    config.bufferMultiplierBasis === "expenses" ? monthlyExpenses : monthlyIncome;
  const rawMinimum = Math.max(
    (config.bufferFloor || 0) * Math.max(1, memberCount),
    (multiplierBasis ?? 0) * (config.bufferMultiplier || 0),
  );
  // QL-127 — a ÷12 side makes the product fractional, while the buffer is
  // typed as a whole amount and the UI prints the minimum rounded: round up
  // to the whole amount (via cents first, so float noise on an exact figure
  // does not add one) so the figure shown is the figure that passes. The
  // fixed buffer is used literally and never goes through this.
  const derivedMinimum = Math.ceil(Math.round(rawMinimum * 100) / 100);
  const bufferMinimum = fixedBuffer ?? derivedMinimum;
  const enteredBuffer = has(input.liquidityBuffer) ? input.liquidityBuffer : null;
  const bufferMode = resolveLiquidityBufferMode(input);
  const customBuffer = fixedBuffer === null && bufferMode === "custom";
  // Under a fixed buffer (or the minimum mode) the effective buffer can never
  // sit below the minimum, so the below-minimum block is unreachable — the
  // completion engine, which only reads this flag, needs no change of its own.
  const bufferBelowMinimum =
    customBuffer && enteredBuffer !== null && enteredBuffer < bufferMinimum;
  const liquidityBufferMissing = customBuffer && enteredBuffer === null;
  const liquidityBuffer =
    fixedBuffer ??
    (customBuffer ? Math.max(enteredBuffer ?? bufferMinimum, bufferMinimum) : bufferMinimum);

  const shortTermExpenses =
    config.shortTermExpensesEnabled && has(input.shortTermExpenses) ? input.shortTermExpenses : 0;

  const idSet = new Set(config.assetFieldIds ?? []);
  const investablePool = (input.assetEntries ?? [])
    .filter((entry) => idSet.has(entry.typeId))
    .reduce((sum, entry) => sum + (entry.amount || 0), 0);

  const availableForInvestments = Math.max(0, investablePool - liquidityBuffer - shortTermExpenses);

  return {
    monthlyIncome,
    monthlyExpenses,
    monthlySurplus,
    bufferMinimum,
    liquidityBuffer,
    bufferBelowMinimum,
    // Additive — absent means the derived path, as on every pre-QL-107 block.
    ...(fixedBuffer !== null ? { fixedBufferApplied: true } : {}),
    // QL-127 — additive likewise: absent means typed / entered.
    ...(incomeFromEntries ? { incomeFromEntries: true } : {}),
    ...(expensesFromEntries ? { expensesFromEntries: true } : {}),
    ...(liquidityBufferMissing ? { liquidityBufferMissing: true } : {}),
    // QL-127 — the modes the block was computed under, for callers that
    // cannot read the model (AI tools, public API partners) and for the
    // widget, which reads the resolved buffer mode here instead of inferring.
    inputFrequency: config.inputFrequency ?? "monthly",
    bufferMultiplierBasis: config.bufferMultiplierBasis ?? "income",
    liquidityBufferMode: bufferMode,
    shortTermExpenses,
    investablePool,
    availableForInvestments,
  };
}

/**
 * QL-127 — the buffer mode the engine runs under: the stored mode when it is
 * one of the two literals, else the pre-QL-127 inference (custom iff a buffer
 * was entered). Only the literals count, so a stored value the service never
 * saw (an old row, a bypassed caller) heals to the inference instead of
 * silently reading as "minimum".
 */
export function resolveLiquidityBufferMode(
  input: Pick<FinancialSituationInput, "liquidityBuffer" | "liquidityBufferMode">,
): LiquidityBufferMode {
  if (input.liquidityBufferMode === "custom" || input.liquidityBufferMode === "minimum") {
    return input.liquidityBufferMode;
  }
  return has(input.liquidityBuffer) ? "custom" : "minimum";
}

/**
 * QL-127 — the triangle sides the financial situation's own entries supply,
 * both MONTHLY: income entries are stored annual (÷ 12); expenses are the
 * monthly commitments plus the liabilities' monthly payments — the same
 * definition the savings-rate metric uses. A side is null until at least one
 * contributing amount is positive, so the seeded empty rows of the required
 * fields never lock the typed input; all finite amounts are then summed. Only `computeCapacity` calls it;
 * the widget reads the `*FromEntries` flags on the result to decide which
 * inputs are read-only. Exported for its tests.
 */
export function capacityTriangleFromEntries(
  input: Pick<FinancialSituationInput, "incomeEntries" | "liabilityEntries" | "commitmentEntries">,
): { monthlyIncome: number | null; monthlyExpenses: number | null } {
  const sum = (amounts: (number | null | undefined)[]): number | null => {
    const present = amounts.filter((v): v is number => has(v));
    // Locked only once a positive amount exists (the seeded empty rows of the
    // required fields never lock the typed input); then every finite amount is
    // summed, negative ones included, exactly as the totals and the savings
    // rate sum them, so the locked side matches the Summary tab.
    return present.some((v) => v > 0) ? present.reduce((total, v) => total + v, 0) : null;
  };
  const annualIncome = sum((input.incomeEntries ?? []).map((entry) => entry.amount));
  const monthlyExpenses = sum([
    ...(input.commitmentEntries ?? []).map((entry) => entry.amount),
    ...(input.liabilityEntries ?? []).map((entry) => entry.monthlyPayment),
  ]);
  return {
    monthlyIncome: annualIncome === null ? null : annualIncome / 12,
    monthlyExpenses,
  };
}

/**
 * QL-266 — whether the model switches the capacity calculator on for at least
 * one client type. The flow editor uses it to warn a designer who enables the
 * capacity section on a component whose model will never render it.
 */
export function hasCapacityVariantEnabled(
  settings: FinancialSituationModelSettings | null | undefined,
): boolean {
  const capacity = settings?.investmentCapacity;
  return (
    capacity?.person?.enabled === true ||
    capacity?.company?.enabled === true ||
    capacity?.group?.enabled === true
  );
}

/**
 * Pick the capacity variant config matching the instance's client type.
 *
 * QL-140 — the FS path (service, completion engine, widget preview) resolves
 * the RAW type via `fsClientType`, so a group reads its own variant. Only
 * classification and AML screening still collapse Group to Person, and neither
 * calls this.
 */
export function capacityVariantFor(
  settings: FinancialSituationModelSettings | null | undefined,
  clientType: string,
): InvestmentCapacityVariantConfig | null {
  const capacity = settings?.investmentCapacity;
  const variant =
    clientType === "Company"
      ? capacity?.company
      : clientType === "Group"
        ? capacity?.group
        : capacity?.person;
  return variant?.enabled ? variant : null;
}

/** The typed triangle keys each input frequency reads; the other set is not used. */
export const CAPACITY_TRIANGLE_KEYS = {
  monthly: ["monthlyIncome", "monthlyExpenses", "monthlySurplus"],
  annual: ["annualIncome", "annualExpenses"],
} as const satisfies Record<CapacityInputFrequency, readonly (keyof FinancialSituationInput)[]>;

type CapacityTriangleKey = (typeof CAPACITY_TRIANGLE_KEYS)[CapacityInputFrequency][number];

/**
 * The triangle keys `input` carries a value for (null and absent do not
 * count) that the variant's input frequency does not read — the monthly keys
 * under `annual`, the annual keys under `monthly`. Mixing both sets is always
 * one of these. Empty without a variant: no frequency applies, so the keys are
 * inert. The save path refuses a non-empty result, so a caller cannot send a
 * figure that is then silently not used.
 */
export function wrongFrequencyCapacityKeys(
  input: Partial<Pick<FinancialSituationInput, CapacityTriangleKey>>,
  config: InvestmentCapacityVariantConfig | null,
): CapacityTriangleKey[] {
  if (!config) return [];
  const other = config.inputFrequency === "annual" ? "monthly" : "annual";
  return CAPACITY_TRIANGLE_KEYS[other].filter((key) => input[key] != null);
}

/**
 * `input` without the triangle keys the variant's input frequency does not
 * read. The widget sends this, so a value typed under the other frequency
 * before the model changed is dropped instead of refused; without a variant
 * the input is returned unchanged.
 */
export function withoutWrongFrequencyCapacityKeys<T extends Partial<FinancialSituationInput>>(
  input: T,
  config: InvestmentCapacityVariantConfig | null,
): T {
  if (!config) return input;
  const other = config.inputFrequency === "annual" ? "monthly" : "annual";
  const result = { ...input };
  for (const key of CAPACITY_TRIANGLE_KEYS[other]) delete result[key];
  return result;
}

/**
 * The input frequency the client type's capacity calculator takes, or null
 * when the calculator is off for it (then neither triangle set is read). The
 * /fields endpoints report it, so an integrator knows which set to send before
 * the first save.
 */
export function capacityInputFrequencyFor(
  settings: FinancialSituationModelSettings | null | undefined,
  clientType: string,
): CapacityInputFrequency | null {
  const variant = capacityVariantFor(settings, clientType);
  return variant ? (variant.inputFrequency ?? "monthly") : null;
}
